import React, { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { TicketKey } from 'flow/types/EntityKeys';
import { TicketStatus } from 'flow/types/Tickets';
import { selectTicketState } from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { updateTicketState } from 'flow/actions/tickets-action';
import { segmentTrack } from 'flow/actions/segment-actions';
import DropdownMenuComponent from 'flow/components/ui-components/DropdownMenuComponent';
import useAppContext from 'flow/hooks/useAppContext';
import useTicketAction from 'flow/hooks/useTicketAction';

const STATUS_i18_KEYS: Record<TicketStatus, string> = Object.freeze({
    open: 'pbx.open',
    closed: 'tickets.closed',
});

const statusOptions = [
    { key: 'open', label: STATUS_i18_KEYS['open'], labelIsI18Key: true },
    { key: 'closed', label: STATUS_i18_KEYS['closed'], labelIsI18Key: true },
];

interface Props {
    ticketKey: TicketKey;
    contextForSegment: string;
}

const StatusOptionsItem = ({ ticketKey, contextForSegment }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();

    const ticketStatusKey = useAppSelector((state) =>
        selectTicketState(state, ticketKey),
    );
    const canSetState = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'setState'),
    );

    const [
        isPerformingSetStatusAPIRequest,
        setIsPerformingSetStatusAPIRequest,
    ] = useState(false);

    const [
        statusKeyToDisplayDuringPostRequest,
        setStatusKeyToDisplayDuringPostRequest,
    ] = useState<TicketStatus | undefined>(undefined);

    const onStatusSelected = useCallback(
        (statusSelected: TicketStatus) => {
            setStatusKeyToDisplayDuringPostRequest(statusSelected);
            setIsPerformingSetStatusAPIRequest(true);
            dispatch(
                segmentTrack(TICKET_STRINGS.USER_CHANGED_STATUS, {
                    component: contextForSegment,
                }),
            );
            runTicketAction(
                updateTicketState(ticketKey, statusSelected),
            ).finally(() => {
                setIsPerformingSetStatusAPIRequest(false);
                setStatusKeyToDisplayDuringPostRequest(undefined);
            });
        },
        [dispatch, contextForSegment, ticketKey],
    );

    return (
        <div>
            <div className="mb-1.5 text-xs font-bold">
                {i18next.t('status.status')}
            </div>
            <DropdownMenuComponent
                options={statusOptions}
                title={
                    statusKeyToDisplayDuringPostRequest
                        ? i18next.t(
                              STATUS_i18_KEYS[
                                  statusKeyToDisplayDuringPostRequest
                              ],
                          )
                        : i18next.t(STATUS_i18_KEYS[ticketStatusKey])
                }
                onItemSelected={onStatusSelected}
                menuButtonClassName="dark:bg-gray-200"
                disabled={!canSetState || isPerformingSetStatusAPIRequest}
            />
        </div>
    );
};

export default React.memo(StatusOptionsItem);
